'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import GoogleLoginButton from './(auth)/components/GoogleLoginButton';
import data from './data/en.json';
import TextModal from './common/components/modals/general/TextModal';

export default function HomePage(): React.JSX.Element {
  const router = useRouter();
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [modalData, setModalData] = useState({ title: '', content: '' });

  useEffect(() => {
    if (loggedIn) {
      router.replace('/student');
    }
  }, [loggedIn, router]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePos({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const getGradientStyles = () => {
    const { x, y } = mousePos;
    return {
      background: `radial-gradient(
        at ${40 + (x / window.innerWidth) * 30}% 
        ${60 + (y / window.innerHeight) * 30}%, 
        rgb(239,79,45) 5%, 
        rgb(24,49,161) 81%, 
        rgb(1,36,151) 100%
      )`,
      transition: 'background-position 0.1s ease-out',
    };
  };

  const getWarpingStyles = () => {
    const { x, y } = mousePos;
    return {
      background: `radial-gradient(
        at ${(x / window.innerWidth) * 20}% 
        ${(y / window.innerHeight) * 10}%, 
        rgba(103,155,252,0) 63%, 
        rgba(39,86,240,0.6) 70%, 
        rgb(1,36,151) 100%
      )`,
      filter: `hue-rotate(${(x / window.innerWidth) * 20 - 10}deg) brightness(1.1)`,
      transition: 'filter 0.2s ease-out, background-position 0.1s ease-out',
    };
  };

  const openModal = (isTOS: boolean): void => {
    setModalData(isTOS ? data.termsOfUse : data.privacyPolicy);
    dispatch(setModalVisible(true));
    dispatch(setModalName('TermsPolicy'));
  };

  const welcomeItems = [
    {
      title: 'Level UP',
      description: 'Personalized AI learning for quick mastery.',
    },
    {
      title: 'Unlock Potential',
      description: 'Drive skill development for lasting success.',
    },
    {
      title: 'Limitless Knowledge',
      description: 'Unlock a wealth of knowledge with intelligent AI.',
    },
  ];

  return (
    <div
      className='absolute left-0 top-0 w-full h-[100vh] flex flex-col md:flex-row justify-center items-center overflow-hidden'
      style={getGradientStyles()}
    >
      {/* Left panel - hidden on mobile only */}
      <div className='hidden md:flex md:w-[40vw] lg:w-[35vw] h-full flex-col backdrop-blur-md bg-surface_white space-y-20 md:space-y-0 lg:space-y-0 [@media(min-width:1200px)]:space-y-0 p-4 md:py-6 md:px-6 lg:p-6 lg:py-6 [@media(min-width:1200px)]:p-6 [@media(min-width:1200px)]:py-6'>
        <div className='w-full flex flex-col px-3 md:px-0 lg:px-4 md:mb-14 [@media(min-width:853px)]:mb-14 lg:mb-14 [@media(min-width:1200px)]:px-4 [@media(min-width:1200px)]:mb-14'>
          <div className='w-[35%] md:w-[25%] lg:w-[35%] [@media(min-width:1200px)]:w-[35%]'>
            <img src='/branding/assistiv/assistiv-logo.png' alt='Logo' className='w-full' />
          </div>
        </div>

        <div className='w-full flex flex-col py-6 md:py-0 md:mb-[80px] [@media(min-width:853px)]:mb-[80px] lg:py-0 lg:mb-[50px] [@media(min-width:1025px)]:!mb-[20px] [@media(min-width:1200px)]:py-0 [@media(min-width:1200px)]:!mb-[20px]'>
          <h3 className='font-bold text-[34px] md:text-[26px] [@media(min-width:853px)]:text-[34px] lg:text-[26px] [@media(min-width:1025px)]:!text-[32px] [@media(min-width:1200px)]:!text-[32px] text-center md:text-left lg:text-left [@media(min-width:1200px)]:text-left md:leading-tight lg:leading-tight [@media(min-width:1200px)]:leading-tight lg:px-4 [@media(min-width:1200px)]:px-3'>
            Your Journey Starts Here.
          </h3>
        </div>

        <div className='w-full flex flex-col pb-20 md:pb-0 lg:pb-0 px-10 md:px-0 lg:px-4 [@media(min-width:1200px)]:px-3 [@media(min-width:1200px)]:pb-0 space-y-4 md:space-y-16 md:pt-[60px] [@media(min-width:853px)]:space-y-22 [@media(min-width:853px)]:pt-[80px] lg:space-y-10 lg:pt-[35px] [@media(min-width:1025px)]:!space-y-4 [@media(min-width:1025px)]:!pt-[15px] [@media(min-width:1200px)]:!pt-[15px] [@media(min-width:1200px)]:!space-y-4'>
          {welcomeItems.map(({ title, description }, i) => (
            <div
              key={i}
              className='w-full flex flex-col md:space-y-3 [@media(min-width:853px)]:space-y-4 lg:space-y-2 [@media(min-width:1025px)]:!space-y-0.5 [@media(min-width:1200px)]:!space-y-0.5'
            >
              <div className='fit-content mr-auto'>
                <h2 className='md:text-[28px] [@media(min-width:853px)]:text-[38px] lg:text-[26px] [@media(min-width:1025px)]:!text-[30px] [@media(min-width:1200px)]:!text-[30px] md:font-bold lg:font-bold [@media(min-width:1200px)]:font-bold md:leading-tight lg:leading-tight [@media(min-width:1200px)]:leading-tight'>
                  {title}
                </h2>
              </div>
              <h4 className='md:text-[20px] [@media(min-width:853px)]:text-[28px] lg:text-[18px] [@media(min-width:1025px)]:!text-[20px] [@media(min-width:1200px)]:!text-[20px] md:leading-normal lg:leading-relaxed [@media(min-width:1200px)]:leading-normal'>
                {description}
              </h4>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div
        className='w-full md:w-[60vw] md:[@media(max-width:899px)]:w-[60vw] lg:w-[65vw] h-full flex flex-col justify-center items-center px-6 md:px-0'
        style={getWarpingStyles()}
      >
        <div className='flex flex-col space-y-6 md:space-y-10 md:[@media(max-width:899px)]:space-y-6 items-center md:items-left fit-content mt-0 md:mt-[-6rem] md:[@media(max-width:899px)]:mt-[-2rem] p-8 md:pt-2 md:px-10 md:pb-10 md:[@media(max-width:899px)]:p-7 rounded-[32px] md:rounded-[40px] md:[@media(max-width:899px)]:rounded-[24px] backdrop-blur-md bg-surface_gray_25 w-full max-w-[340px] md:w-auto md:max-w-none'>
          {/* Logo - mobile only */}
          <div className='flex md:hidden w-20 mx-auto'>
            <img src='/branding/assistiv/assistiv-logo.png' alt='Logo' className='w-full' />
          </div>

          <h1 className='text-2xl md:text-5xl md:[@media(max-width:899px)]:text-3xl font-bold leading-tight md:leading-[150%] text-center md:text-left md:self-start'>
            Sign in to Assistiv
            <br />
            <hr className='border-text_gray_800' />
          </h1>
          <div className='flex flex-col space-y-2 w-full md:w-auto self-center md:self-start'>
            <div className='w-full md:w-auto flex justify-center md:justify-start'>
              <GoogleLoginButton isAdmin={true} />
            </div>
          </div>
          <span className='text-sm md:text-[16px] md:[@media(max-width:899px)]:text-xs text-center md:text-left'>
            New To Assistiv? Start by signing in with a Google account.
          </span>
        </div>
      </div>
      <footer className='fixed bottom-0 flex w-screen items-center justify-center opacity-50 pb-4 md:pb-0'>
        <a
          className='mx-4 text-sm md:text-base hover:text-white hover:underline cursor-pointer'
          onClick={() => {
            openModal(true);
          }}
        >
          {data.termsOfUse.title}
        </a>
        <a
          className='mx-4 text-sm md:text-base hover:text-white hover:underline cursor-pointer'
          onClick={() => {
            openModal(false);
          }}
        >
          {data.privacyPolicy.title}
        </a>
      </footer>
      <TextModal modalName='TermsPolicy' title={modalData.title} displayText={modalData.content} />
    </div>
  );
}
